import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { 
  getExtensionFromUrl, 
  generateTempFileName 
} from './fileUtils';

// Platform detection
const isNative = Capacitor.isNativePlatform();
const isWeb = !isNative;

// Share file using native platform sharing
export const shareFile = async (url, fileName = 'document', title = 'Share File') => {
  if (!isNative) {
    // Fallback for web - create a download link
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { success: true, method: 'download' };
  }

  try {
    // Download file to temporary cache directory
    const tempFileName = generateTempFileName(fileName, getExtensionFromUrl(url));
    
    const downloadResult = await Filesystem.downloadFile({
      path: tempFileName,
      url: url,
      directory: Directory.Cache,
    });

    if (!downloadResult.path) {
      throw new Error('Failed to download file for sharing.');
    }

    // Get the proper URI for sharing
    const uriResult = await Filesystem.getUri({
      directory: Directory.Cache,
      path: tempFileName
    });

    // Share the file using native platform sharing
    const shareResult = await Share.share({
      title: title,
      text: `Sharing ${fileName}`,
      url: uriResult.uri,
      dialogTitle: `Share ${fileName}`
    });

    return { 
      success: true, 
      method: 'native_share',
      result: shareResult 
    };

  } catch (error) {
    // Check if this is a user cancellation (which is not an error)
    if (error.message && (
      error.message.includes('cancelled') || 
      error.message.includes('canceled') ||
      error.message.includes('dismissed') ||
      error.message.includes('User cancelled') ||
      error.message.includes('User canceled')
    )) {
      return { 
        success: true, 
        method: 'cancelled',
        result: null 
      };
    }
    
    // Only fallback to browser for actual errors, not cancellations
    window.open(url, '_blank');
    return { 
      success: false, 
      method: 'browser_fallback', 
      error: error.message 
    };
  }
};

// Share multiple files (for arrays of URLs)
export const shareMultipleFiles = async (urls, fileNames = [], title = 'Share Files') => {
  if (!isNative) {
    // For web, share each file individually
    const results = [];
    for (let i = 0; i < urls.length; i++) {
      const fileName = fileNames[i] || `file-${i + 1}`;
      const result = await shareFile(urls[i], fileName, title);
      results.push(result);
    }
    return results;
  }

  try {
    // Download all files to temporary cache
    const downloadPromises = urls.map(async (url, index) => {
      const fileName = fileNames[index] || `file-${index + 1}`;
      const tempFileName = generateTempFileName(fileName, getExtensionFromUrl(url));
      
      await Filesystem.downloadFile({
        path: tempFileName,
        url: url,
        directory: Directory.Cache,
      });
      
      // Get the proper URI for sharing
      const uriResult = await Filesystem.getUri({
        directory: Directory.Cache,
        path: tempFileName
      });
      
      return uriResult.uri;
    });

    const fileUris = await Promise.all(downloadPromises);

    // Share multiple files (platform dependent)
    const shareResult = await Share.share({
      title: title,
      text: `Sharing ${urls.length} files`,
      url: fileUris[0], // Some platforms only support single file
      dialogTitle: `Share ${urls.length} files`
    });

    return { 
      success: true, 
      method: 'native_share_multiple',
      result: shareResult,
      fileCount: urls.length
    };

  } catch (error) {
    // Fallback to sharing files individually
    const results = [];
    for (let i = 0; i < urls.length; i++) {
      const fileName = fileNames[i] || `file-${i + 1}`;
      const result = await shareFile(urls[i], fileName, title);
      results.push(result);
    }
    return results;
  }
};

// Check if sharing is available on the platform
export const isSharingAvailable = () => {
  return isNative && Share.share;
};

// Get platform-specific sharing options
export const getSharingOptions = () => {
  const platform = Capacitor.getPlatform();
  
  return {
    platform,
    isNative,
    isWeb,
    canShareFiles: isNative,
    canShareMultiple: platform === 'ios', // iOS generally has better multi-file support
    supportedFormats: ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv']
  };
}; 